import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Award, Globe, Cpu, Code, Network, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ANIMATION_VARIANTS, HERO_CONTENT } from '../constants';
import { 
  useFloatingParticles,
  usePerformanceMonitoring,
  useOptimizedBackground
} from '../hooks/useAnimations';
import { GlassCard, BenefitCard, StyledIcon } from '../components/animations';
import { handlePageNavigation } from '../utils/navigation';

const AboutPage: React.FC = () => {
  usePerformanceMonitoring('AboutPage');
  
  // Global floating particles for the entire page (mandatory pattern)
  const { particlesRef, containerClasses: particlesContainerClasses, particles } = useFloatingParticles(50);

  // Optimized backgrounds for alternating sections
  const { backgroundClasses: heroBackgroundClasses, backgroundStyle: heroBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: missionBackgroundClasses, backgroundStyle: missionBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: differenceBackgroundClasses, backgroundStyle: differenceBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: teamBackgroundClasses, backgroundStyle: teamBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: visionBackgroundClasses, backgroundStyle: visionBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: ctaBackgroundClasses, backgroundStyle: ctaBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={ANIMATION_VARIANTS.fadeIn}
      className="min-h-screen relative"
    >
      {/* Global Floating Particles Background */}
      <div ref={particlesRef} className={`${particlesContainerClasses} fixed inset-0 z-30`}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={particle.classes}
            style={particle.style}
          />
        ))}
      </div>

      <div className="relative z-20">
        {/* Hero Section */}
        <section className="section-padding relative text-white">
          {/* Hero Background - Normal orientation */}
          <div 
            className={heroBackgroundClasses}
            style={heroBackgroundStyle}
          />
          
          <div className="container relative z-40 pt-32 mb-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="max-w-4xl mx-auto text-center"
            >
              <motion.div 
                className="flex items-center justify-center mb-6"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                <StyledIcon 
                  icon={Users} 
                  size="xl" 
                  color="primary" 
                  variant="static" 
                />
              </motion.div>

              <motion.h1
                variants={ANIMATION_VARIANTS.slideUp}
                className="heading-1 mb-6"
              >
                About <span className="text-primary-gradient">MTAI</span>
              </motion.h1>
              
              <motion.p
                variants={ANIMATION_VARIANTS.slideUp}
                className="hero-subtitle text-center mb-12 max-w-3xl mx-auto"
              >
                Welcome to MTAI, one of the first companies in Southeast Asia to offer end-to-end AI compute and solutions, 
                providing businesses with a complete suite of AI capabilities that revolutionize how they operate, grow, and innovate.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Who We Are Section */}
        <section className="section-padding relative text-white">
          {/* Mission Background - Rotated orientation for alternating effect */}
          <div 
            className={missionBackgroundClasses}
            style={{
              ...missionBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            {/* Mission Statement with Image */}
            <GlassCard className="p-8 lg:p-12 rounded-3xl mb-16">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.staggerChildren}
                >
                  <motion.h2 
                    className="text-3xl lg:text-4xl font-bold mb-6"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    Who <span className="text-primary-gradient">We Are</span>
                  </motion.h2>
                  <motion.p 
                    className="text-lg text-gray-300 mb-6 leading-relaxed"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    MTAI is a pioneering AI solutions provider based in Malaysia, delivering world-class AI compute infrastructure, 
                    software, and services to enterprises looking to leverage the transformative potential of AI.
                  </motion.p>
                  <motion.p 
                    className="text-lg text-gray-300 leading-relaxed"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    Our mission is to empower businesses by offering scalable, high-performance AI technology tailored 
                    to meet specific industry needs across Southeast Asia.
                  </motion.p>
                </motion.div>
                
                <motion.div 
                  className="relative"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.slideUp}
                >
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                    <img 
                      src="/assets/images/about/mission.png"
                      alt="MTAI - Who We Are"
                      className="w-full h-80 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/20 via-transparent to-primary-500/10"></div>
                    
                    <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 border border-primary-400/30">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-white text-xs font-medium">Pioneering AI Solutions</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </GlassCard>

            {/* Our Capabilities Cards */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <div className="grid md:grid-cols-3 gap-8">
                <BenefitCard
                  icon={Cpu}
                  title="AI Infrastructure"
                  description="Cutting-edge AI compute clusters and high-performance infrastructure designed for machine learning and deep learning workloads."
                  color="blue"
                  delay={0.1}
                />
                <BenefitCard
                  icon={Code}
                  title="Custom Solutions"
                  description="Tailored AI applications and software solutions built to solve your specific business challenges across multiple industries."
                  color="emerald"
                  delay={0.2}
                />
                <BenefitCard
                  icon={Globe}
                  title="Regional Expertise"
                  description="Serving healthcare, finance, retail, manufacturing, and telecommunications sectors across Southeast Asia with local market knowledge."
                  color="purple"
                  delay={0.3}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* What Sets Us Apart Section */}
        <section className="section-padding relative text-white">
          {/* Difference Background - Normal orientation */}
          <div 
            className={differenceBackgroundClasses}
            style={differenceBackgroundStyle}
          />
          
          <div className="container relative z-40">
            <motion.div 
              className="mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.h2 
                className="heading-2 mb-16 text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                What Sets <span className="text-primary-gradient">Us Apart</span>
              </motion.h2>
              <div className="grid md:grid-cols-2 gap-8">
                <BenefitCard
                  icon={Target}
                  title="First End-to-End AI Solution in Southeast Asia"
                  description="We are the region's first AI provider to offer a comprehensive end-to-end service. From AI compute clusters to custom-built AI software solutions and deployment, MTAI offers everything your business needs to stay ahead in the rapidly evolving digital landscape."
                  color="blue"
                  delay={0.1}
                />
                <BenefitCard
                  icon={Cpu}
                  title="State-of-the-Art Infrastructure"
                  description="Our AI compute services are built on state-of-the-art hardware and cloud solutions, giving businesses access to advanced GPU and high-performance computing clusters that are fully optimized for AI, machine learning, and deep learning workloads."
                  color="emerald"
                  delay={0.2}
                />
                <BenefitCard
                  icon={Code}
                  title="Custom AI Solutions"
                  description="Our team of skilled Software Solutions Engineers works closely with clients to develop custom AI applications tailored to their unique challenges. Whether it's intelligent automation, predictive analytics, or customer-centric AI tools, MTAI designs solutions that deliver tangible business results."
                  color="purple"
                  delay={0.3}
                />
                <BenefitCard
                  icon={Network}
                  title="Seamless AI Deployment"
                  description="We don't just build AI solutions; we deploy them. With deep expertise in AI infrastructure, we ensure smooth integration of AI technologies into your existing systems, whether you're operating on-premises, in the cloud, or a hybrid environment."
                  color="orange"
                  delay={0.4}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Our Team Section */}
        <section className="section-padding relative text-white">
          {/* Team Background - Rotated orientation */}
          <div 
            className={teamBackgroundClasses}
            style={{
              ...teamBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <motion.div 
              className="mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.h2 
                className="heading-2 mb-8 text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Our <span className="text-primary-gradient">Expert Team</span>
              </motion.h2>
              
              <motion.p 
                className="body-large text-center mb-16 max-w-3xl mx-auto"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                At MTAI, our success is built on the collective expertise of our highly experienced team of Network Engineers and Software Solutions Engineers. 
                These professionals bring decades of hands-on experience in designing, building, and deploying complex networks and AI-driven solutions across various industries.
              </motion.p>
              
              <div className="grid lg:grid-cols-2 gap-8">
                <BenefitCard
                  icon={Network}
                  title="Network Engineers"
                  description="Our network engineering team ensures that AI infrastructure operates seamlessly and reliably. They specialize in designing high-performance, secure networks capable of handling the massive data flow and processing demands of AI workloads, from compute clusters to cloud infrastructure."
                  color="cyan"
                  delay={0.1}
                />
                <BenefitCard
                  icon={Code}
                  title="Software Solutions Engineers"
                  description="Our software team is at the forefront of AI development, building custom applications that leverage machine learning, natural language processing, and predictive analytics to solve real-world business problems. They ensure that AI solutions not only perform optimally but also integrate smoothly with your existing systems."
                  color="purple"
                  delay={0.2}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Our Vision & Mission Section */}
        <section className="section-padding relative text-white">
          {/* Vision Background - Normal orientation */}
          <div
            className={visionBackgroundClasses}
            style={visionBackgroundStyle}
          />

          <div className="container relative z-40">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Our Vision Card */}
              <GlassCard className="p-8 lg:p-12 rounded-3xl text-center">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.staggerChildren}
                >
                  <motion.div
                    className="flex items-center justify-center mb-6"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    <StyledIcon
                      icon={Globe}
                      size="xl"
                      color="primary"
                      variant="static"
                    />
                  </motion.div>

                  <motion.h2
                    className="text-3xl lg:text-4xl font-bold mb-6"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    Our <span className="text-primary-gradient">Vision</span>
                  </motion.h2>

                  <motion.p
                    className="text-lg text-gray-300 leading-relaxed"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    To anchor Malaysia's digital and AI future with infrastructure the nation can trust, own, and grow with.
                  </motion.p>
                </motion.div>
              </GlassCard>

              {/* Our Mission Card */}
              <GlassCard className="p-8 lg:p-12 rounded-3xl text-center">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.staggerChildren}
                >
                  <motion.div
                    className="flex items-center justify-center mb-6"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    <StyledIcon
                      icon={Target}
                      size="xl"
                      color="primary"
                      variant="static"
                    />
                  </motion.div>

                  <motion.h2
                    className="text-3xl lg:text-4xl font-bold mb-6"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    Our <span className="text-primary-gradient">Mission</span>
                  </motion.h2>

                  <motion.p
                    className="text-lg text-gray-300 leading-relaxed"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    MTAI designs, owns, and operates Malaysia's integrated digital and AI infrastructure, enabling governments and enterprises to deploy AI at national scale with security, resilience, and long-term efficiency.
                  </motion.p>
                </motion.div>
              </GlassCard>
            </div>
          </div>
        </section>

        {/* Why Work With MTAI Section */}
        <section className="section-padding relative text-white">
          {/* Benefits Background - Rotated orientation */}
          <div 
            className={teamBackgroundClasses}
            style={{
              ...teamBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <motion.div 
              className="mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.h2 
                className="heading-2 mb-16 text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Why Work With <span className="text-primary-gradient">MTAI?</span>
              </motion.h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <BenefitCard
                  icon={Globe}
                  title="Local Expertise with Global Standards"
                  description="We are proud to serve as a local AI partner for businesses in Malaysia and Southeast Asia, combining deep local market knowledge with global best practices in AI technology."
                  color="emerald"
                  delay={0.1}
                />
                <BenefitCard
                  icon={Award}
                  title="Proven Track Record"
                  description="With a team of engineers who have successfully implemented AI solutions across various industries, we bring a wealth of experience and a proven methodology for ensuring successful AI adoption."
                  color="blue"
                  delay={0.2}
                />
                <BenefitCard
                  icon={Target}
                  title="End-to-End Solutions"
                  description="From consultation and design to deployment and support, MTAI provides a complete AI service that covers every stage of the AI lifecycle."
                  color="purple"
                  delay={0.3}
                />
                <BenefitCard
                  icon={CheckCircle}
                  title="Scalable and Flexible Services"
                  description="Whether you're just starting with AI or looking to scale existing operations, we offer solutions that grow with your business, ensuring that you're always equipped with the latest and most powerful AI tools."
                  color="orange"
                  delay={0.4}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding relative text-white">
          {/* CTA Background - Normal orientation */}
          <div 
            className={ctaBackgroundClasses}
            style={ctaBackgroundStyle}
          />
          
          <div className="container relative z-40">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.h2 
                className="heading-2 mb-6"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Join Us in Shaping the Future with <span className="text-primary-gradient">AI</span>
              </motion.h2>
              <motion.p 
                className="body-large mb-8"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                At MTAI, we're not just providing AI technology—we're driving innovation. 
                We invite you to partner with us and unlock the full potential of artificial intelligence for your business. 
                Together, we can build a future where AI transforms industries, enhances experiences, and creates opportunities across Southeast Asia.
              </motion.p>
              <motion.div 
                className="text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                <div className="flex flex-col sm:flex-row gap-4 mt-auto justify-center items-center">
                    <Link
                        to="/contact"
                        className="btn-futuristic"
                        onClick={handlePageNavigation}
                    >
                        Contact Us Today
                    </Link>
                    <Link
                        to="/services"
                        className="btn-outline"
                        onClick={handlePageNavigation}
                    >
                        Explore Services
                    </Link>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </motion.div>
  );
};

export default AboutPage;
